import React, { useMemo, useState, useEffect } from 'react'
import { Button, Col, Dropdown, Icon, Menu, Row } from 'antd'
import moment from 'moment'

import { currencyMask } from 'src/components/Formik'
import { RequestQuoteAir, RequestQuoteAirFare } from 'src/types'
import { Text, ToolIcon } from 'src/components/Ant'
import Images from 'src/config/images'

import { ConnectionsText, SecondaryText, StrongText } from '../../styles'

type Offers = Pick<
  RequestQuoteAirFare,
  'baggage_amount' | 'baggage_weight' | 'baggage' | 'family' | 'total_value'
> & { currencyCode: string }

function HeaderItemHistory({ item }: { item: RequestQuoteAir[] }) {
  const [defaultOption, setDefaultOption] = useState<Offers>(undefined)
  const stops =
    item[0].stops === 0 ? (
      <Text onlyText title="web.services.airs.form.stop_no" />
    ) : (
      <Text
        onlyText
        title={JSON.stringify({
          message: 'web.services.airs.form.stop',
          params: { count: item[0].stops }
        })}
      />
    )

  const groupFamily = useMemo(() => {
    const familyList: Offers[] = []
    if (item.length) {
      item.forEach(quote => {
        quote.request_quote_air_fares.forEach(
          ({
            family,
            baggage,
            baggage_amount,
            baggage_weight,
            total_value
          }) => {
            familyList.push({
              family,
              baggage,
              baggage_amount,
              baggage_weight,
              total_value,
              currencyCode: quote.currency.code
            })
          }
        )
      })
    }
    return familyList
  }, [item])

  useEffect(() => {
    if (groupFamily.length) {
      setDefaultOption(
        groupFamily.sort((a, b) => a.total_value - b.total_value)[0]
      )
    }
  }, [groupFamily])

  function handleSelectValue(value: any) {
    setDefaultOption({ ...defaultOption, total_value: Number(value.key) || 0 })
    value.domEvent.stopPropagation()
  }

  function handleFamilyChange(value: any) {
    const lower = groupFamily.filter(filter => filter.family === value.key)[0]
    setDefaultOption({
      ...lower,
      family: value.key
    })
    value.domEvent.stopPropagation()
  }

  return (
    <Row align="middle" gutter={24} justify="center" type="flex">
      <Col span={3}>
        <Row align="middle" justify="center" type="flex">
          <img
            alt={item[0].company}
            src={Images.airlines[item[0].company] || Images.noImage}
            style={{ maxHeight: 45, maxWidth: 45 }}
          />
        </Row>
      </Col>
      <Col span={5}>
        <Row
          align="middle"
          justify="center"
          type="flex"
          style={{ flexDirection: 'column' }}
        >
          <SecondaryText>
            {moment.unix(item[0].departure_date).utc().format('DD/MM/YYYY')}
          </SecondaryText>
          <StrongText>
            {moment.unix(item[0].departure_date).utc().format('HH:mm')}
          </StrongText>
          <SecondaryText>{item[0].air_origin}</SecondaryText>
        </Row>
      </Col>
      <Col span={5}>
        <Row
          align="middle"
          justify="center"
          type="flex"
          style={{ flexDirection: 'column' }}
        >
          <ConnectionsText connections={item[0].stops}>{stops}</ConnectionsText>
          <SecondaryText>
            <Text title="web.services.airs.form.flight" onlyText />{' '}
            {item[0].request_quote_air_flights[0].flight_code}
          </SecondaryText>
        </Row>
      </Col>
      <Col span={5}>
        <Row
          align="middle"
          justify="center"
          type="flex"
          style={{ flexDirection: 'column' }}
        >
          <SecondaryText>
            {moment.unix(item[0].arrival_date).utc().format('DD/MM/YYYY')}
          </SecondaryText>
          <StrongText>
            {moment.unix(item[0].arrival_date).utc().format('HH:mm')}
          </StrongText>
          <SecondaryText>{item[0].air_destiny}</SecondaryText>
        </Row>
      </Col>
      <Col span={6}>
        <Dropdown
          overlay={
            <Menu>
              {groupFamily
                .filter(
                  (offer, index) =>
                    groupFamily.findIndex(
                      item => item.family === offer.family
                    ) === index
                )
                .map(offer => {
                  const baggage = offer.baggage ? (
                    <ToolIcon
                      // @ts-ignore
                      title={
                        <>
                          <Text
                            onlyText
                            title={JSON.stringify({
                              message:
                                'web.services.airs.form.fare.baggage-amount',
                              params: { count: offer.baggage_amount }
                            })}
                          />{' '}
                          <Text
                            onlyText
                            title={JSON.stringify({
                              message:
                                'web.services.airs.form.fare.baggage-weight',
                              params: { count: offer.baggage_weight }
                            })}
                          />
                        </>
                      }
                      type="shopping"
                    />
                  ) : (
                    <Icon type="close-square" />
                  )
                  return (
                    <Menu.Item key={offer.family} onClick={handleFamilyChange}>
                      {`${offer.family} `}
                      {baggage}
                    </Menu.Item>
                  )
                })}
            </Menu>
          }
          trigger={['hover']}
        >
          <Button
            block
            icon="down"
            onClick={event => {
              // If you don't want click extra trigger collapse, you can prevent this:
              event.stopPropagation()
            }}
            style={{ color: '#34a853', marginBottom: 8 }}
            type="dashed"
          >
            {defaultOption?.family || ''}
          </Button>
        </Dropdown>
        <Dropdown
          overlay={
            <Menu onClick={handleSelectValue}>
              {groupFamily
                .filter(item => item.family === defaultOption?.family)
                .map((item, index) => {
                  const totalValue = `${item.currencyCode} ${
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value: item.total_value
                    }).maskedValue
                  }`
                  return (
                    <Menu.Item key={index} title={totalValue}>
                      {totalValue}
                    </Menu.Item>
                  )
                })}
            </Menu>
          }
          trigger={['hover']}
        >
          <Button
            block
            icon="down"
            style={{ color: '#34a853', marginBottom: 8 }}
            type="dashed"
          >
            {`${defaultOption?.currencyCode} ${
              currencyMask({
                formatStr: true,
                precision: 2,
                value: defaultOption?.total_value
              }).maskedValue
            }` || ''}
          </Button>
        </Dropdown>
      </Col>
    </Row>
  )
}

export default HeaderItemHistory
